import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Tooltip, IconButton, Tab, Tabs, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import { Shield, Refresh } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const LEVEL_CFG = {
  critical: { color: '#b71c1c', bg: '#ffebee', label: 'Critical', dot: '🔴' },
  high:     { color: '#e65100', bg: '#fff3e0', label: 'High',     dot: '🟠' },
  medium:   { color: '#f57f17', bg: '#fffde7', label: 'Medium',   dot: '🟡' },
  low:      { color: '#2e7d32', bg: '#e8f5e9', label: 'Low',      dot: '🟢' },
  none:     { color: '#9e9e9e', bg: '#f5f5f5', label: 'None',     dot: '⚪' },
};

const DIM_ICONS: Record<string, string> = {
  turnover:   '🚪',
  skills:     '🧩',
  compliance: '📋',
  succession: '🎯',
  training:   '📚',
};

type Level = 'critical' | 'high' | 'medium' | 'low' | 'none';

function RiskCell({ score, level, finding, link, onClick }: {
  score: number; level: Level; finding: string; link: string; onClick: () => void;
}) {
  const cfg = LEVEL_CFG[level];
  return (
    <Tooltip title={`Score: ${score}/100 — ${finding}`} arrow>
      <Box onClick={onClick} sx={{
        bgcolor: cfg.bg,
        border: `1px solid ${cfg.color}40`,
        borderRadius: 1,
        p: 0.75,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': { bgcolor: cfg.color + '25', transform: 'scale(1.04)' },
        minWidth: 70,
      }}>
        <Typography variant="caption" fontWeight={800} color={cfg.color}>{score}</Typography>
        <Box sx={{ width: '100%', height: 3, bgcolor: '#e0e0e0', borderRadius: 1, mt: 0.25 }}>
          <Box sx={{ width: `${score}%`, height: 3, bgcolor: cfg.color, borderRadius: 1 }} />
        </Box>
        <Typography sx={{ fontSize: '0.55rem', color: cfg.color, mt: 0.25 }}>{cfg.label}</Typography>
      </Box>
    </Tooltip>
  );
}

function OrgScoreGauge({ score, level }: { score: number; level: Level }) {
  const cfg = LEVEL_CFG[level];
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 80, height: 80, borderRadius: '50%',
        background: `conic-gradient(${cfg.color} ${score * 3.6}deg, #e0e0e0 0deg)` }}>
        <Box sx={{ width: 62, height: 62, borderRadius: '50%', bgcolor: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <Typography variant="h6" fontWeight={800} color={cfg.color}>{score}</Typography>
          <Typography variant="caption" sx={{ fontSize: '0.5rem', color: cfg.color }}>/100</Typography>
        </Box>
      </Box>
      <Chip size="small" label={`${cfg.dot} ${cfg.label} Risk`}
        sx={{ mt: 0.5, bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.65rem' }} />
    </Box>
  );
}

export default function OrgRisk() {
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState(0);
  const [error, setError]     = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch('/api/org-risk')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const depts: any[]   = data?.depts ?? [];
  const orgDims: any[] = data?.orgDims ?? [];
  const summary        = data?.summary ?? {};
  const orgOverall     = data?.orgOverall ?? 0;
  const orgLevel: Level = data?.orgLevel ?? 'none';
  const criticalDepts  = depts.filter(d => d.overallLevel === 'critical');

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#37474f', width: 48, height: 48 }}><Shield /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Org Risk Heat Map</Typography>
            <Typography variant="body2" color="text.secondary">
              5-dimension risk index across all departments: turnover · skills · compliance · succession · training
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load} disabled={loading}><Refresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {criticalDepts.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>{criticalDepts.length} department{criticalDepts.length > 1 ? 's are' : ' is'} at critical risk:</strong>{' '}
          {criticalDepts.map(d => d.department).join(', ')}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* Org-wide scorecard */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 'auto' as any }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <OrgScoreGauge score={orgOverall} level={orgLevel} />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: true as any }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Organisation-wide Risk Profile</Typography>
                <Grid container spacing={1}>
                  {orgDims.map(d => {
                    const cfg = LEVEL_CFG[d.level as Level];
                    return (
                      <Grid size={{ xs: 6, sm: 4 }} key={d.key}>
                        <Tooltip title={`Click to view ${d.label}`}>
                          <Box onClick={() => navigate(d.link)} sx={{ p: 1, borderRadius: 1, bgcolor: cfg.bg,
                            borderLeft: `3px solid ${cfg.color}`, cursor: 'pointer',
                            '&:hover': { opacity: 0.85 } }}>
                            <Typography variant="caption" display="block" color="text.secondary" noWrap>
                              {DIM_ICONS[d.key]} {d.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color={cfg.color}>{d.avgScore}/100</Typography>
                            <LinearProgress variant="determinate" value={d.avgScore}
                              sx={{ height: 3, mt: 0.25, borderRadius: 1,
                                '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                          </Box>
                        </Tooltip>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 'auto' as any }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {Object.entries(summary).map(([k, v]: [string, any]) => {
                    const cfg = LEVEL_CFG[k as Level];
                    return (
                      <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: cfg.color }} />
                        <Typography variant="body2">{v} {cfg.label}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label="Heat Map" />
            <Tab label="Ranked Table" />
            <Tab label="By Dimension" />
          </Tabs>

          {/* Heat Map Grid */}
          {tab === 0 && (
            <Paper sx={{ overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, minWidth: 160, bgcolor: '#fafafa' }}>Department</TableCell>
                    {orgDims.map(d => (
                      <TableCell key={d.key} align="center" sx={{ minWidth: 80, bgcolor: '#fafafa' }}>
                        <Typography variant="caption" fontWeight={700}>{DIM_ICONS[d.key]}</Typography>
                        <Typography variant="caption" display="block" fontWeight={700} sx={{ fontSize: '0.6rem' }}>{d.label}</Typography>
                        <Chip size="small" label={`org: ${d.avgScore}`}
                          sx={{ bgcolor: LEVEL_CFG[d.level as Level].bg, color: LEVEL_CFG[d.level as Level].color,
                            fontSize: '0.5rem', height: 16 }} />
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ fontWeight: 700, minWidth: 80, bgcolor: '#fafafa' }}>Overall</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {depts.map(dept => (
                    <TableRow key={dept.department} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%',
                            bgcolor: LEVEL_CFG[dept.overallLevel as Level].color }} />
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{dept.department}</Typography>
                            <Typography variant="caption" color="text.secondary">{dept.headcount} staff</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {dept.dimensions.map((dim: any) => (
                        <TableCell key={dim.dimension} align="center" sx={{ p: 0.75 }}>
                          <RiskCell
                            score={dim.score} level={dim.level}
                            finding={dim.finding} link={dim.link}
                            onClick={() => navigate(dim.link)} />
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ p: 0.75 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="body2" fontWeight={700}
                            color={LEVEL_CFG[dept.overallLevel as Level].color}>{dept.overallRisk}</Typography>
                          <Chip size="small"
                            label={`${LEVEL_CFG[dept.overallLevel as Level].dot} ${LEVEL_CFG[dept.overallLevel as Level].label}`}
                            sx={{ bgcolor: LEVEL_CFG[dept.overallLevel as Level].bg,
                              color: LEVEL_CFG[dept.overallLevel as Level].color, fontSize: '0.55rem', height: 18 }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ p: 1.5, bgcolor: '#fafafa', display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary">Click any cell to drill in →</Typography>
                {Object.entries(LEVEL_CFG).filter(([k]) => k !== 'none').map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: v.color }} />
                    <Typography variant="caption">{v.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {/* Ranked table */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rank</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell>Overall Risk</TableCell>
                    <TableCell>Highest Risk Dimension</TableCell>
                    <TableCell>Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {depts.map((d, i) => {
                    const topDim = d.dimensions.reduce((best: any, cur: any) => cur.score > best.score ? cur : best, d.dimensions[0] ?? {});
                    const cfg = LEVEL_CFG[d.overallLevel as Level];
                    return (
                      <TableRow key={d.department} hover sx={{ bgcolor: d.overallLevel === 'critical' ? '#fff5f5' : undefined }}>
                        <TableCell>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem',
                            bgcolor: i < 3 ? cfg.color : '#e0e0e0', color: i < 3 ? '#fff' : '#546e7a' }}>
                            {i + 1}
                          </Avatar>
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.department}</Typography></TableCell>
                        <TableCell align="right">{d.headcount}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={d.overallRisk}
                              sx={{ flex: 1, height: 8, borderRadius: 3,
                                '& .MuiLinearProgress-bar': { bgcolor: cfg.color } }} />
                            <Typography variant="body2" fontWeight={700} color={cfg.color}>{d.overallRisk}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {topDim.dimension && (
                            <Chip size="small"
                              label={`${DIM_ICONS[topDim.dimension]} ${topDim.label}: ${topDim.score}`}
                              onClick={() => navigate(topDim.link)}
                              sx={{ bgcolor: LEVEL_CFG[topDim.level as Level]?.bg,
                                color: LEVEL_CFG[topDim.level as Level]?.color,
                                cursor: 'pointer', fontSize: '0.6rem' }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`${cfg.dot} ${cfg.label}`}
                            sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.6rem' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* By dimension */}
          {tab === 2 && (
            <Grid container spacing={2}>
              {orgDims.map(dim => {
                const sorted = [...depts].sort((a, b) => {
                  const sa = a.dimensions.find((x: any) => x.dimension === dim.key)?.score ?? 0;
                  const sb = b.dimensions.find((x: any) => x.dimension === dim.key)?.score ?? 0;
                  return sb - sa;
                });
                const cfg = LEVEL_CFG[dim.level as Level];
                return (
                  <Grid size={{ xs: 12, md: 6 }} key={dim.key}>
                    <Paper sx={{ p: 2, borderTop: `3px solid ${cfg.color}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '1.3rem' }}>{DIM_ICONS[dim.key]}</Typography>
                          <Box>
                            <Typography variant="subtitle2" fontWeight={700}>{dim.label}</Typography>
                            <Chip size="small" label={`Org avg: ${dim.avgScore}/100`}
                              sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.6rem' }} />
                          </Box>
                        </Box>
                        <Chip size="small" label="View details →" onClick={() => navigate(dim.link)}
                          sx={{ cursor: 'pointer', bgcolor: '#f5f5f5', fontSize: '0.6rem' }} />
                      </Box>
                      {sorted.slice(0, 6).map(dept => {
                        const dimData = dept.dimensions.find((x: any) => x.dimension === dim.key);
                        if (!dimData) return null;
                        const dc = LEVEL_CFG[dimData.level as Level];
                        return (
                          <Box key={dept.department} sx={{ mb: 0.75 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                              <Typography variant="caption" fontWeight={600}>{dept.department}</Typography>
                              <Typography variant="caption" color={dc.color} fontWeight={700}>{dimData.score}</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={dimData.score}
                              sx={{ height: 6, borderRadius: 3,
                                '& .MuiLinearProgress-bar': { bgcolor: dc.color } }} />
                            <Typography variant="caption" color="text.secondary">{dimData.finding}</Typography>
                          </Box>
                        );
                      })}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
